import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { NOTIFICATIONS_CHANGED } from '../lib/events';
import type {
  NotificationDTO,
  NotificationListResponse,
} from '../../shared/notification-types';

/**
 * Sprint C4 — Polling-Hook für Notifications.
 *
 * Responsibilities:
 *  - 30s setInterval `GET /api/notifications?status=unread`
 *  - AbortController-coalescing (cancel pending bei neuem tick / event)
 *  - visibility/focus refetch mit min-10s-throttle (Codex R1 #14)
 *  - monotonic `requestIdRef` (A18/Codex R1 #11) — stale responses dropped
 *  - `NOTIFICATIONS_CHANGED` Custom-Event listener für authoritative-GET-
 *    after-mutation (A19/Codex R1 #12 — KEIN lokal-decrement)
 *  - Cleanup on unmount (interval + abort + remove listeners)
 *
 * State-Shape:
 *  - `notifications`: server-snapshot DTO[] (max 50, sortiert createdAt DESC)
 *  - `unreadCount`: server-snapshot number (0 wenn keine unread)
 *  - `error`: last error message, oder null wenn last fetch erfolgreich
 *  - `loading`: true beim first-fetch nur (subsequent fetches sind background)
 */

const POLL_INTERVAL_MS = 30_000;
const REFETCH_THROTTLE_MS = 10_000;

export interface UseNotificationsPollState {
  notifications: NotificationDTO[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
}

export function useNotificationsPoll(): UseNotificationsPollState {
  const [state, setState] = useState<UseNotificationsPollState>({
    notifications: [],
    unreadCount: 0,
    loading: true,
    error: null,
  });

  // Monotonic request-id: jeder fetch increments. Stale response (requestId
  // != current) wird verworfen → eliminiert race zwischen poll-N und
  // mutation-triggered refetch (A18/Codex R1 #11).
  const requestIdRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  // Letzter erfolgreicher fetch in ms (Date.now()). visibility/focus-refetch
  // skippt, wenn diff < REFETCH_THROTTLE_MS (Codex R1 #14).
  const lastSuccessAtRef = useRef(0);

  const refetch = useCallback(() => {
    // Cancel pending — AbortController-coalescing.
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    // Increment monotonic. Wenn response ankommt + requestIdRef.current
    // ≠ thisRequestId → ignorieren (stale).
    requestIdRef.current += 1;
    const thisRequestId = requestIdRef.current;

    (async () => {
      try {
        const data = await api<NotificationListResponse>(
          '/api/notifications?status=unread',
          { signal: ctrl.signal },
        );
        if (ctrl.signal.aborted) return;
        // Stale-response check: nur akzeptieren wenn dieser request-id noch
        // der current ist (keine mutation in-flight zwischendurch).
        if (thisRequestId !== requestIdRef.current) return;

        lastSuccessAtRef.current = Date.now();
        setState({
          notifications: data.notifications,
          unreadCount: data.unreadCount,
          loading: false,
          error: null,
        });
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (ctrl.signal.aborted) return;
        if (thisRequestId !== requestIdRef.current) return;
        const msg = err instanceof Error ? err.message : 'Unknown error';
        setState((prev) => ({ ...prev, loading: false, error: msg }));
      }
    })();
  }, []);

  // Throttled refetch — für visibility/focus-events. Skipt wenn last
  // successful fetch jünger als REFETCH_THROTTLE_MS.
  const throttledRefetch = useCallback(() => {
    const now = Date.now();
    if (now - lastSuccessAtRef.current < REFETCH_THROTTLE_MS) return;
    refetch();
  }, [refetch]);

  useEffect(() => {
    // Initial fetch.
    refetch();

    // Background-polling alle 30s.
    const interval = window.setInterval(refetch, POLL_INTERVAL_MS);

    // Visibility-/Focus-refetch (Codex R1 #14 sleep-laptop resume).
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') throttledRefetch();
    };
    const handleFocus = () => throttledRefetch();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    // Authoritative-GET-after-mutation (A19/Codex R1 #12).
    // NICHT throttled — Mutations sollen IMMER refetchen, sonst sehen
    // User die eigene mark-read-Action nicht reflektiert.
    window.addEventListener(NOTIFICATIONS_CHANGED, refetch);

    return () => {
      window.clearInterval(interval);
      abortRef.current?.abort();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener(NOTIFICATIONS_CHANGED, refetch);
    };
  }, [refetch, throttledRefetch]);

  return state;
}
